"use client"

import type { ReactNode } from "react"
import Image from "next/image"
import Link from "next/link"
import { useLocale } from "next-intl"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useCountry } from "@/components/layout/country-provider"
import { track } from "@/lib/analytics"
import { bookingUrlForCountry } from "@/lib/booking"
import styles from "./cta-banner.module.css"

export interface CtaBannerProps {
  title: string
  subtitle?: string
  /** Image URL for the background. Ignored when `background` is set. */
  imageSrc?: string
  /** CSS `background` shorthand value to use in place of the image
   *  (e.g. multi-stop gradient). When provided, the `Image` is not
   *  rendered. Useful when the design specifies a pure-gradient panel
   *  rather than a photo behind the heading. */
  background?: string
  /** Primary CTA. Provide `href` for a link, or `onClick` for a button
   *  (e.g. opening the waitlist modal on pre-launch markets). `onClick`
   *  takes precedence when both are set. */
  primaryCta?: { label: string; href?: string; onClick?: () => void }
  secondaryCta?: { label: string; href: string }
  /** "center" = soft central dimming with a lower mobile text area.
   *  "dim" (default) = flat bg-black/50; "gradient" = softer top-to-bottom
   *  linear overlay; "none" = no overlay (use with `textColor="dark"` on
   *  bright imagery where the picture itself provides enough contrast). */
  overlay?: "dim" | "gradient" | "center" | "none"
  /** Optional trust row rendered below the CTA buttons, 2-col on desktop. */
  trustItems?: Array<{ icon: ReactNode; label: string }>
  className?: string
  overlayClassName?: string
  /** Extra classes on the background image. Useful for object-position / transform tweaks per page. */
  imageClassName?: string
  /** When true, primary + secondary CTAs stretch full width on mobile (no max-w-md cap). */
  fullWidthCtasMobile?: boolean
  /** Foreground colour. "light" (default) is white text for dim/dark imagery;
   *  "dark" is neutral-900 text for bright imagery (sky, sun, light backgrounds). */
  textColor?: "light" | "dark"
  /** Override the default `type-display-md` heading class — pass a different
   *  display token when a specific banner needs more or less visual weight. */
  titleClassName?: string
  /** Paint the primary CTA white (`on-dark`) instead of the DS primary, as
   *  the membership-first homepage does on its closing photo band. */
  primaryOnDark?: boolean
  /** When set, clicks on either link CTA fire `cta_click` with this location. */
  trackingLocation?: string
}

export function CtaBanner({
  title,
  subtitle,
  imageSrc,
  background,
  primaryCta,
  secondaryCta,
  overlay = "dim",
  trustItems,
  className,
  overlayClassName,
  imageClassName,
  fullWidthCtasMobile = false,
  textColor = "light",
  titleClassName,
  primaryOnDark = false,
  trackingLocation,
}: CtaBannerProps) {
  // DE (checkup): this closing "book check-up" module should jump straight to
  // the DE booking app (blood is bookable) with country=de&locale=de, rather
  // than to /pricing or a placeholder "#". Rewrite any link-style primary CTA
  // on DE; a waitlist (onClick) primary is left untouched. AHD-6680.
  const { code: country } = useCountry()
  const locale = useLocale()
  const effectivePrimary =
    primaryCta && !primaryCta.onClick && country === "de"
      ? { ...primaryCta, href: bookingUrlForCountry(country, locale) }
      : primaryCta

  const isDark = textColor === "dark"
  const isCenterBanner = overlay === "center"
  const ctaSize = isCenterBanner ? "lg" : "default"
  const ctaClassName = cn(
    "w-full sm:w-auto",
    isCenterBanner &&
      "bg-neutral-950 hover:bg-neutral-900 focus-visible:ring-neutral-400"
  )
  const headingCls = isDark ? "text-neutral-900" : "text-white"
  const subtitleCls = isDark ? "text-neutral-700" : "text-white/80"
  const trustItemCls = isDark ? "text-neutral-700" : "text-white/80"
  const trustIconCls = isDark ? "text-neutral-600" : "text-white/75"
  const outlineBtnCls = isDark
    ? "border-neutral-900/60 text-neutral-900 hover:border-neutral-900 hover:bg-transparent hover:shadow-[inset_0_0_0_999px_rgba(0,0,0,0.06)]"
    : "border-white/60 text-white hover:border-white hover:bg-transparent hover:shadow-[inset_0_0_0_999px_rgba(255,255,255,0.08)]"
  return (
    <section
      className={cn(
        "relative flex min-h-[400px] w-full items-center justify-center overflow-hidden md:min-h-[667px]",
        overlay === "center" && styles.centerBanner,
        className
      )}
    >
      {/* Background — either a CSS background (gradient panel) or a
          full-bleed Image. `background` wins when both are set. */}
      {background ? (
        <div
          aria-hidden="true"
          className={cn("absolute inset-0", imageClassName)}
          style={{ background }}
        />
      ) : (
        imageSrc && (
          <Image
            src={imageSrc}
            alt=""
            aria-hidden="true"
            fill
            sizes={
              overlay === "center"
                ? "(max-width: 767px) 1800px, 100vw"
                : "100vw"
            }
            className={cn(
              "object-cover",
              overlay === "center" && styles.centerImage,
              imageClassName
            )}
            quality={90}
          />
        )
      )}

      {/* Overlay */}
      {overlay !== "none" && (
        <div
          aria-hidden="true"
          className={cn(
            "absolute inset-0",
            overlay === "dim" && "bg-black/50",
            overlay === "center" && styles.centerOverlay,
            overlayClassName
          )}
          style={
            overlay === "gradient"
              ? {
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.42) 50%, rgba(0,0,0,0.55) 100%)",
                }
              : undefined
          }
        />
      )}

      {/* Content */}
      <div
        className={cn(
          "relative z-10 flex flex-col items-center px-8 py-12 text-center md:py-0",
          overlay === "center" && styles.centerContent,
          // When CTAs need to stretch full-width on mobile, the content div
          // itself must be w-full — otherwise it shrinks to the heading/subtitle
          // intrinsic widths and the buttons stop short of the viewport edges.
          fullWidthCtasMobile && "w-full md:w-auto"
        )}
      >
        <h2
          className={cn(
            "max-w-2xl",
            titleClassName ?? "type-display-md",
            headingCls
          )}
        >
          {title}
        </h2>
        {subtitle && (
          <p className={cn("type-body-lg mt-4 max-w-xl", subtitleCls)}>
            {subtitle}
          </p>
        )}

        {(primaryCta || secondaryCta) && (
          <div
            className={cn(
              "mt-8 flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4",
              fullWidthCtasMobile
                ? "-mx-8 px-4 sm:mx-0 sm:px-0"
                : "max-w-md sm:max-w-none"
            )}
          >
            {effectivePrimary &&
              (effectivePrimary.onClick ? (
                <Button
                  type="button"
                  onClick={effectivePrimary.onClick}
                  data-testid="cta-banner-primary"
                  size={ctaSize}
                  className={ctaClassName}
                >
                  {effectivePrimary.label}
                </Button>
              ) : (
                <Link
                  href={effectivePrimary.href ?? "#"}
                  data-testid="cta-banner-primary"
                  className="block w-full sm:inline-block sm:w-auto"
                  onClick={
                    trackingLocation
                      ? () =>
                          track.ctaClick(
                            effectivePrimary.label,
                            trackingLocation,
                            effectivePrimary.href
                          )
                      : undefined
                  }
                >
                  <Button
                    size={ctaSize}
                    variant={primaryOnDark ? "on-dark" : "primary"}
                    className={ctaClassName}
                  >
                    {effectivePrimary.label}
                  </Button>
                </Link>
              ))}
            {secondaryCta && (
              <Link
                href={secondaryCta.href}
                data-testid="cta-banner-secondary"
                className="block w-full sm:inline-block sm:w-auto"
                onClick={
                  trackingLocation
                    ? () =>
                        track.ctaClick(
                          secondaryCta.label,
                          trackingLocation,
                          secondaryCta.href
                        )
                    : undefined
                }
              >
                <Button
                  variant={isCenterBanner ? "tonal" : "outline"}
                  size={ctaSize}
                  className={cn(
                    "w-full sm:w-auto",
                    isCenterBanner
                      ? "bg-white/30 text-white backdrop-blur-[2px] hover:bg-white/40"
                      : outlineBtnCls
                  )}
                >
                  {secondaryCta.label}
                </Button>
              </Link>
            )}
          </div>
        )}

        {trustItems && trustItems.length > 0 && (
          <ul className="mx-auto mt-10 flex w-fit max-w-3xl flex-wrap justify-center gap-x-8 gap-y-3">
            {trustItems.map((item, i) => (
              <li
                key={i}
                className={cn(
                  "type-body-md flex items-center gap-2.5",
                  trustItemCls
                )}
              >
                <span className={cn("shrink-0", trustIconCls)}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
